/**
 * E2E Flow Version History Modal
 * Issue #415: Display version history for individual E2E flows
 * Issue #437: Show AI suggestions (missed steps and suggestions) with categories
 *
 * Features:
 * - Lists all versions of a flow
 * - Shows version metadata (date, source, step count)
 * - Allows viewing historical versions
 * - Highlights current/latest version
 * - Shows AI suggestions with missed/suggested filter tabs
 * - Displays code references for missed steps
 */

import { useState, useEffect } from 'react';
import { api } from '../../services/api';

// Issue #437: Missing step type from enhancement history
interface MissingStep {
  afterStepId: string;
  suggestedType: string;
  suggestedName: string;
  description: string;
  reasoning: string;
  confidence: number;
  evidenceLevel?: string;
  evidenceSources?: Array<{
    type: string;
    filePath?: string;
    lineStart?: number;
    lineEnd?: number;
    description: string;
  }>;
  category?: 'missed' | 'suggested';
  codeReference?: {
    functionName?: string;
    variableName?: string;
    apiEndpoint?: string;
    codeSnippet?: string;
  };
}

interface FlowVersionSummary {
  id: string;
  flowId: string;
  version: number;
  isLatest: boolean;
  source: string;
  createdAt: string;
  flowName: string;
  stepCount: number;
  isComplete: boolean;
  // Issue #425: Enhancement details for AI-enhanced versions
  enhancement?: {
    id: string;
    depth: string;
    promptSent?: string;
    aiResponse?: string;
    tokensUsed?: number;
    completenessScore?: number;
  };
}

// Issue #437: Enhancement history entry with missing steps
interface EnhancementHistoryEntry {
  id: string;
  flowId: string;
  enhancementDepth: string;
  missingSteps?: MissingStep[];
  completenessScore?: number;
  tokensUsed?: number;
  applied: boolean;
  createdAt: string;
}

interface E2EFlowVersionHistoryProps {
  projectId: string;
  flowId: string;
  flowName: string;
  onClose: () => void;
  onViewVersion?: (versionId: string) => void;
}

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  analysis: { label: 'Analysis', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  enhancement: { label: 'AI Enhanced', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  regeneration: { label: 'Regenerated', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  revert: { label: 'Reverted', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  manual: { label: 'Manual Edit', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

// Issue #437: Category badge configuration
const CATEGORY_CONFIG: Record<'missed' | 'suggested', { label: string; color: string; icon: string }> = {
  missed: {
    label: 'Missed Step',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    icon: '⚠',
  },
  suggested: {
    label: 'Suggestion',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: '💡',
  },
};

// Issue #437: Evidence level badge configuration
const EVIDENCE_CONFIG: Record<string, { label: string; color: string }> = {
  strong: { label: 'Strong', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  inferred: { label: 'Inferred', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-400' },
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function E2EFlowVersionHistory({
  projectId,
  flowId,
  flowName,
  onClose,
  onViewVersion,
}: E2EFlowVersionHistoryProps) {
  const [versions, setVersions] = useState<FlowVersionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Issue #425: Track which version's AI details are expanded
  const [expandedAiDetails, setExpandedAiDetails] = useState<Set<string>>(new Set());
  // Issue #437: Enhancement history with missing steps
  const [enhancementHistory, setEnhancementHistory] = useState<EnhancementHistoryEntry[]>([]);
  // Issue #437: Category filter for missing steps
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'missed' | 'suggested'>('all');
  // Issue #437: Track which version's suggestions are expanded
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch versions and enhancement history in parallel
        const [versionsResponse, historyResponse] = await Promise.all([
          api.getE2EFlowVersions(projectId, flowId),
          api.getE2EFlowEnhancementHistory(projectId, flowId, true), // Include details
        ]);
        setVersions(versionsResponse.versions);
        setEnhancementHistory(historyResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load versions');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [projectId, flowId]);

  // Issue #425: Toggle AI details expansion
  const toggleAiDetails = (versionId: string) => {
    setExpandedAiDetails(prev => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  // Issue #437: Toggle suggestions expansion
  const toggleSuggestions = (versionId: string) => {
    setExpandedSuggestions(prev => {
      const next = new Set(prev);
      if (next.has(versionId)) {
        next.delete(versionId);
      } else {
        next.add(versionId);
      }
      return next;
    });
  };

  // Issue #437: Get enhancement for a version
  const getEnhancementForVersion = (version: FlowVersionSummary): EnhancementHistoryEntry | undefined => {
    if (version.source !== 'enhancement' || !version.enhancement) return undefined;
    return enhancementHistory.find(e => e.id === version.enhancement?.id);
  };

  // Issue #437: Filter missing steps by category
  const filterMissingSteps = (steps: MissingStep[] | undefined): MissingStep[] => {
    if (!steps) return [];
    if (categoryFilter === 'all') return steps;
    return steps.filter(s => s.category === categoryFilter);
  };

  // Issue #437: Count steps by category
  const countByCategory = (steps: MissingStep[] | undefined): { missed: number; suggested: number } => {
    if (!steps) return { missed: 0, suggested: 0 };
    return steps.reduce(
      (acc, step) => {
        if (step.category === 'missed') acc.missed++;
        else acc.suggested++;
        return acc;
      },
      { missed: 0, suggested: 0 }
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              Version History
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {flowName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">{error}</p>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500 dark:text-slate-400">
                No version history available yet.
              </p>
              <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                Version history is created when you regenerate or enhance a flow.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => {
                const sourceInfo = SOURCE_LABELS[version.source] || {
                  label: version.source,
                  color: 'bg-slate-100 text-slate-700',
                };
                const enhancement = getEnhancementForVersion(version);
                const missingSteps = enhancement?.missingSteps || [];
                const counts = countByCategory(missingSteps);
                const filteredSteps = filterMissingSteps(missingSteps);
                const hasSuggestions = missingSteps.length > 0;

                return (
                  <div
                    key={version.id}
                    className={`p-3 rounded-lg border ${
                      version.isLatest
                        ? 'border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 dark:text-slate-200">
                            Version {version.version}
                          </span>
                          {version.isLatest && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-500 text-white rounded">
                              Current
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${sourceInfo.color}`}>
                            {sourceInfo.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 dark:text-slate-400">
                          <span>{version.stepCount} steps</span>
                          <span>
                            {version.isComplete ? (
                              <span className="text-green-600 dark:text-green-400">Complete</span>
                            ) : (
                              <span className="text-amber-600 dark:text-amber-400">UI-Only</span>
                            )}
                          </span>
                          <span>{formatRelativeTime(version.createdAt)}</span>
                        </div>
                      </div>
                      {onViewVersion && !version.isLatest && (
                        <button
                          onClick={() => onViewVersion(version.id)}
                          className="px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                        >
                          View
                        </button>
                      )}
                    </div>

                    {/* Issue #437: AI Suggestions Section */}
                    {version.source === 'enhancement' && hasSuggestions && (
                      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                        <button
                          onClick={() => toggleSuggestions(version.id)}
                          className="flex items-center gap-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 transition-colors"
                        >
                          <span>{expandedSuggestions.has(version.id) ? '▼' : '▶'}</span>
                          <span>📋 AI Suggestions</span>
                          <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-purple-100 dark:bg-purple-900/30 rounded">
                            {missingSteps.length} total
                          </span>
                          {counts.missed > 0 && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded">
                              ⚠ {counts.missed} missed
                            </span>
                          )}
                          {counts.suggested > 0 && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                              💡 {counts.suggested} new
                            </span>
                          )}
                        </button>

                        {expandedSuggestions.has(version.id) && (
                          <div className="mt-2 space-y-2">
                            {/* Filter tabs */}
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-slate-500">Filter:</span>
                              <button
                                onClick={() => setCategoryFilter('all')}
                                className={`px-2 py-0.5 rounded transition-colors ${
                                  categoryFilter === 'all'
                                    ? 'bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200'
                                    : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                                }`}
                              >
                                All ({missingSteps.length})
                              </button>
                              {counts.missed > 0 && (
                                <button
                                  onClick={() => setCategoryFilter('missed')}
                                  className={`px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                    categoryFilter === 'missed'
                                      ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                                      : 'text-slate-500 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                                  }`}
                                >
                                  ⚠ Missed ({counts.missed})
                                </button>
                              )}
                              {counts.suggested > 0 && (
                                <button
                                  onClick={() => setCategoryFilter('suggested')}
                                  className={`px-2 py-0.5 rounded flex items-center gap-1 transition-colors ${
                                    categoryFilter === 'suggested'
                                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                                      : 'text-slate-500 hover:bg-blue-50 dark:hover:bg-blue-900/30'
                                  }`}
                                >
                                  💡 Suggestions ({counts.suggested})
                                </button>
                              )}
                            </div>

                            {/* Missing steps list */}
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {filteredSteps.map((step, idx) => {
                                const category = step.category || 'suggested';
                                const catConfig = CATEGORY_CONFIG[category];
                                const evidenceConfig = step.evidenceLevel
                                  ? EVIDENCE_CONFIG[step.evidenceLevel]
                                  : null;

                                return (
                                  <div
                                    key={`${step.afterStepId}-${idx}`}
                                    className="p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700"
                                  >
                                    <div className="flex items-start gap-2">
                                      <span
                                        className={`px-1.5 py-0.5 text-[10px] font-medium rounded flex items-center gap-1 ${catConfig.color}`}
                                      >
                                        <span>{catConfig.icon}</span>
                                        {catConfig.label}
                                      </span>
                                      {evidenceConfig && (
                                        <span
                                          className={`px-1.5 py-0.5 text-[10px] rounded ${evidenceConfig.color}`}
                                        >
                                          {evidenceConfig.label}
                                        </span>
                                      )}
                                      <span className="px-1.5 py-0.5 text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 rounded">
                                        Conf: {Math.round(step.confidence * 100)}%
                                      </span>
                                    </div>
                                    <div className="mt-1 text-sm font-medium text-slate-800 dark:text-slate-200">
                                      {step.suggestedName}
                                    </div>
                                    <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                      {step.reasoning}
                                    </div>

                                    {/* Issue #437: Code reference for missed steps */}
                                    {category === 'missed' && step.codeReference && (
                                      <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                        <div className="text-[10px] text-slate-400 mb-1">
                                          Code reference (exists in codebase):
                                        </div>
                                        <div className="text-xs bg-slate-100 dark:bg-slate-800 p-2 rounded font-mono space-y-0.5">
                                          {step.codeReference.functionName && (
                                            <div>
                                              <span className="text-slate-400">Function:</span>{' '}
                                              <span className="text-purple-600 dark:text-purple-400">
                                                {step.codeReference.functionName}
                                              </span>
                                            </div>
                                          )}
                                          {step.codeReference.variableName && (
                                            <div>
                                              <span className="text-slate-400">Variable:</span>{' '}
                                              <span className="text-blue-600 dark:text-blue-400">
                                                {step.codeReference.variableName}
                                              </span>
                                            </div>
                                          )}
                                          {step.codeReference.apiEndpoint && (
                                            <div>
                                              <span className="text-slate-400">Endpoint:</span>{' '}
                                              <span className="text-green-600 dark:text-green-400">
                                                {step.codeReference.apiEndpoint}
                                              </span>
                                            </div>
                                          )}
                                          {step.codeReference.codeSnippet && (
                                            <div className="mt-1 p-1 bg-slate-200 dark:bg-slate-700 rounded text-[10px]">
                                              {step.codeReference.codeSnippet}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Evidence sources */}
                                    {step.evidenceSources && step.evidenceSources.length > 0 && (
                                      <div className="mt-1 text-[10px] text-slate-400">
                                        {step.evidenceSources.map((src, i) => (
                                          <div key={i}>
                                            {src.filePath && (
                                              <span className="font-mono">
                                                📍 {src.filePath}
                                                {src.lineStart && `:${src.lineStart}`}
                                                {src.lineEnd && src.lineEnd !== src.lineStart && `-${src.lineEnd}`}
                                              </span>
                                            )}
                                            {!src.filePath && src.description}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Applied status */}
                                    <div className="mt-1 text-[10px]">
                                      {enhancement?.applied ? (
                                        <span className="text-green-600 dark:text-green-400">✓ Applied</span>
                                      ) : (
                                        <span className="text-slate-400">Not applied</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Issue #425: AI Details for enhancement versions (prompt/response) */}
                    {version.source === 'enhancement' && version.enhancement && (
                      <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                        <button
                          onClick={() => toggleAiDetails(version.id)}
                          className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
                        >
                          <span>{expandedAiDetails.has(version.id) ? '▼' : '▶'}</span>
                          <span>🔍 View Prompt/Response</span>
                          {version.enhancement.depth && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-slate-100 dark:bg-slate-700 rounded">
                              {version.enhancement.depth}
                            </span>
                          )}
                        </button>
                        {expandedAiDetails.has(version.id) && (
                          <div className="mt-2 space-y-2">
                            {version.enhancement.tokensUsed && (
                              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                                Tokens: {version.enhancement.tokensUsed}
                                {version.enhancement.completenessScore !== undefined && (
                                  <span className="ml-2">Score: {version.enhancement.completenessScore}%</span>
                                )}
                              </div>
                            )}
                            {version.enhancement.promptSent && (
                              <div>
                                <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                  Prompt Sent to AI
                                </div>
                                <div className="max-h-64 overflow-auto p-3 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                                  <pre className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-mono">
                                    {version.enhancement.promptSent}
                                  </pre>
                                </div>
                              </div>
                            )}
                            {version.enhancement.aiResponse && (
                              <div>
                                <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                                  Raw AI Response
                                </div>
                                <div className="max-h-64 overflow-auto p-3 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700">
                                  <pre className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-mono">
                                    {version.enhancement.aiResponse}
                                  </pre>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
            {versions.length} version{versions.length !== 1 ? 's' : ''} saved
          </p>
        </div>
      </div>
    </div>
  );
}

export default E2EFlowVersionHistory;
